import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable, { Column, DataTableSelectedData, Row } from '@repo/ui/data-table'
import { InformationIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import React, { useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { getTripBookingDispatchPlaces } from '../../../api/trip-booking-dispatch-places/get-trip-booking-dispatch-places'
import { dispatch } from '../../../api/trip-dates/dispatch-individuals'
import { getTripDate } from '../../../api/trip-dates/get-trip-date'
import { getTripDateTripManagers } from '../../../api/trip-dates/get-trip-date-trip-managers'
import { retain } from '../../../api/trip-dates/retain-individuals'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { TripBookingDispatchPlaceIndividualTypeEnum } from '../../../lib/types/models/trip-booking/trip-date'
import { TripBookingDispatchPlace } from '../../../lib/types/models/trip-booking-dispatch-places/trip-booking-dispatch-place'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { DispatchModal } from './dispatch.utils'
import {
  getEnrollmentsDispatchColumns,
  getStaffsDispatchColumns,
  TripDateVariantType
} from './trip-date.utils'
import { extractTripBookingDispatchPlaceIndividualId } from './utils'

type DispatchTableProps = {
  title: string
  variant: 'dispatch' | 'retain'
  version: TripDateVariantType
  tripDateId: number
  tripBookingId?: number
  individualTypes?: TripBookingDispatchPlaceIndividualTypeEnum[]
  onSuccess?: () => void
  onDataChange?: (data: TripBookingDispatchPlace[]) => void
}

export interface DispatchTableRef {
  data: TripBookingDispatchPlace[]
  refetch: () => void
}

export const DispatchTable = React.forwardRef<DispatchTableRef, DispatchTableProps>(
  (
    {
      title,
      variant,
      version,
      tripDateId,
      tripBookingId,
      individualTypes,
      onDataChange,
      onSuccess
    },
    ref
  ) => {
    const { t } = useTranslate()

    const [selectedData, setSelectedData] = useState<TripBookingDispatchPlace[]>([])
    const { modalState, toggleModal, closeModel } = useModalManager<'dispatch'>(['dispatch'])
    const isStaff =
      individualTypes?.includes(TripBookingDispatchPlaceIndividualTypeEnum.AFFECTED_STAFF) ||
      individualTypes?.includes(TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_STAFF) ||
      individualTypes?.includes(
        TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_EXTERNAL_STAFF
      ) ||
      individualTypes?.includes(TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_LEC_STAFF)

    const { data: tripDate } = useQuery({
      queryKey: ['trip-date', tripDateId],
      queryFn: () => getTripDate(tripDateId),
      enabled: variant === 'retain'
    })

    const tripBooking = useMemo(
      () => tripDate?.tripBookings.find((tb) => tb.id === tripBookingId),
      [tripDate, tripBookingId]
    )

    const numberofPlacesReserved = individualTypes?.includes(
      TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT
    )
      ? tripBooking?.numberOfStudentsPlacesReserved
      : tripBooking?.numberOfStaffPlacesReserved

    const { data: dispatchPlaces, refetch } = useQuery({
      queryKey: ['dispatchPlaces', version, variant, tripDateId, individualTypes, tripBookingId],
      queryFn: () =>
        getTripBookingDispatchPlaces<'FULL'>({
          version: 'FULL',
          filter: { tripDateId, individualTypes, tripBookingId, dispatched: variant === 'retain' }
        })
    })

    const { data: tripManagers } = useQuery({
      queryKey: ['tripManagers', tripDateId],
      queryFn: () => getTripDateTripManagers(tripDateId),
      enabled: individualTypes?.includes(TripBookingDispatchPlaceIndividualTypeEnum.AFFECTED_STAFF)
    })

    const { mutateAsync: dispatchMutation, isPending } = useMutation({
      mutationFn: dispatch,
      onError: showAxiosError,
      onSuccess: () => {
        setSelectedData([])
        onSuccess?.()
        if (isStaff) toast.success('Les encadrants ont été dispatchés avec succès')
        else toast.success('Les jeunes ont été dispatchés avec succès')
        closeModel('dispatch')
      }
    })

    const { mutate: retainMutation } = useMutation({
      mutationFn: retain,
      onError: showAxiosError,
      onSuccess: () => {
        setSelectedData([])
        onSuccess?.()
        if (isStaff) toast.success('Les encadrants ont été retirés avec succès')
        else toast.success('Les jeunes ont été retirés avec succès')
      }
    })

    useImperativeHandle(ref, () => ({ data: dispatchPlaces?.data ?? [], refetch }), [
      dispatchPlaces
    ])

    const columns: Column<TripBookingDispatchPlace>[] = [
      ...(individualTypes?.includes(TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT)
        ? getEnrollmentsDispatchColumns(version)
        : getStaffsDispatchColumns(version, t, tripManagers)),
      ...(variant === 'retain'
        ? [
            {
              accessorKey: 'id',
              header: '',
              cell: ({ row }: { row: Row<TripBookingDispatchPlace> }) => (
                <Button
                  label={'Retirer'}
                  variant={'ghost'}
                  onClick={() =>
                    retainMutation({
                      id: tripDateId,
                      input: [
                        {
                          id: extractTripBookingDispatchPlaceIndividualId(row.original),
                          type: row.original.individualType
                        }
                      ]
                    })
                  }
                />
              )
            }
          ]
        : [])
    ]

    const selectedItems: DataTableSelectedData<TripBookingDispatchPlace, undefined> = useMemo(
      () => ({
        isAllSelected: selectedData.length === dispatchPlaces?.data.length,
        rows: selectedData
          .map((item) => dispatchPlaces?.data.find((e) => e.id === item.id))
          .filter((e) => !!e),
        expandedRows: []
      }),
      [selectedData, dispatchPlaces]
    )

    const handleSelectChange = (data: DataTableSelectedData<TripBookingDispatchPlace>) => {
      if (data.isAllSelected && dispatchPlaces && dispatchPlaces.data.length > 0)
        setSelectedData(dispatchPlaces.data)
      else if (data.rows.length > 0) setSelectedData(data.rows)
      else setSelectedData([])
    }

    const disabled = useMemo(() => {
      if (selectedData.length === 0) return true
      if (!individualTypes?.includes(TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT))
        return false

      if (selectedItems.isAllSelected)
        return !dispatchPlaces?.data?.every(
          ({ enrollment }) =>
            enrollment?.enrollmentCenter?.center?.id ===
            dispatchPlaces?.data.at(0)?.enrollment?.enrollmentCenter?.center?.id
        )

      // if not all the tripDateEnrollments is selected, check if the selected tripDateEnrollments have the same center
      return !selectedItems.rows.every(
        ({ enrollment }) =>
          enrollment?.enrollmentCenter?.center?.id ===
          selectedItems.rows.at(0)?.enrollment?.enrollmentCenter?.center?.id
      )
    }, [selectedItems, dispatchPlaces])

    // get the number of places reserved for the trip date (students or staffs)

    useEffect(() => {
      onDataChange?.(dispatchPlaces?.data ?? [])
    }, [dispatchPlaces?.data])

    // reset the selection when switching to another trip booking
    useEffect(() => {
      setSelectedData([])
    }, [tripBookingId])

    return (
      <>
        {variant === 'retain' ? (
          <div className={'flex flex-row gap-2 items-end justify-between'}>
            <div className={'flex flex-row gap-3 items-center'}>
              <Typography variant={'text'}>{title}</Typography>
              <Badge
                variant={isStaff ? 'blue' : 'fushia'}
                size={'medium'}
                text={`${dispatchPlaces?.total ?? 0} dispatchés / ${numberofPlacesReserved ?? 0} places réservées`}
              />
            </div>

            {/* bulk retain actions */}
            <div className={'flex flex-row gap-2 items-end'}>
              <Button
                label={'Retirer'}
                variant={'ghost'}
                disabled={selectedData.length === 0}
                onClick={() =>
                  retainMutation({
                    id: tripDateId,
                    input: selectedData.map((item) => ({
                      id: extractTripBookingDispatchPlaceIndividualId(item),
                      type: item.individualType
                    }))
                  })
                }
              />
              <Badge variant={'grey'} text={`${selectedData.length} sélectionnés`} />
            </div>
          </div>
        ) : (
          <>
            <Typography variant={'text'}>{title}</Typography>

            {/* table actions */}
            <div className={'flex flex-row gap-2 items-end justify-between'}>
              <Button
                label={'Dispatcher dans les réservations'}
                onClick={() => toggleModal('dispatch')}
                disabled={disabled}
              />
              <Badge variant={'grey'} text={`${selectedData.length} sélectionnés`} />
            </div>

            {/* badge to display if the selected data have different centers */}
            {disabled && selectedData.length > 0 && (
              <Badge
                variant={'danger'}
                className={'w-full'}
                iconPosition={'start'}
                text={"Vous devez sélectionner les jeunes d'un même centre"}
                icon={<InformationIcon className={'w-6 h-6'} color={tailwindColors.red.DEFAULT} />}
              />
            )}
          </>
        )}

        <DataTable<TripBookingDispatchPlace>
          selectable
          columns={columns}
          paginated={false}
          manualSorting={false}
          data={dispatchPlaces?.data ?? []}
          selectedData={selectedItems}
          rowCount={dispatchPlaces?.total ?? 0}
          onSelectedDataChange={handleSelectChange}
          display={dispatchPlaces && dispatchPlaces.data.length > 0}
        />

        <DispatchModal
          isOpen={modalState.dispatch}
          tripDateId={tripDateId}
          loading={isPending}
          individualsCount={selectedData.length}
          onCancel={() => closeModel('dispatch')}
          onCreate={async (data) =>
            await dispatchMutation({
              ...data,
              id: tripDateId,
              data: selectedData.map((item) => ({
                id: extractTripBookingDispatchPlaceIndividualId(item),
                type: item.individualType
              }))
            })
          }
        />
      </>
    )
  }
)
